import { useFormik } from 'formik';
import { FC, Fragment, useEffect, useState } from 'react';
import * as yup from 'yup';

import { ImagesApp, avatarDefaultUrl } from '../../constants/images';
import { useCartContext } from '../../hooks/contexts/cart.context';
import { useEventContext } from '../../hooks/contexts/event.context';
import { useI18nContext } from '../../hooks/contexts/i18n.context';
import { useModalContext } from '../../hooks/contexts/modal.context';
import { useUiContext } from '../../hooks/contexts/ui.context';
import { useUserContext } from '../../hooks/contexts/user.context';
import { ComboInNaming, HolderProps } from '../../types/cart';
import formatNumericValue from '../../utils/formatNumericValue';
import DateTimeSessionsTags from '../DateTimeSessionsTags';
import { AlertModalContent } from '../FacialRecognition';
import ErrorAlert from '../Naming/ErrorAlert';
import HolderTicketCheckbox from '../Naming/HolderTicketCheckbox';
import TagNaming from '../Naming/TagNaming';
import UserNaming from '../Naming/UserNaming';
import * as Styles from './naming-combo-card.styles';

type NamingComboCardProps = {
  namingData: ComboInNaming;
  id: string;
  startWithRfcModal: boolean | null;
};

const initialValues = { email : '' };

const NamingComboCard: FC<NamingComboCardProps> = ({
  namingData,
  id,
  startWithRfcModal,
}) => {
  const {
    idSpecificInIdentifyList,
    transferMandatoryFaceRecognition,
    holder,
    faceRecognition,
  } = namingData;

  const sumValues = () => {
    return namingData.comboDates.reduce((acc, date) => {
      const value = date.groups[0].tickets[0].price;
      return acc + (value - (value * date.discount) / 100);
    }, 0);
  };

  const [userNotFound, setUserNotFound] = useState(false);
  const [userHasntBepassToken, setUserHasntBepassToken] = useState(false);

  const { user, getUserByEmailOrDocument } = useUserContext();
  const { translate } = useI18nContext();
  const { event, currencyFormat } = useEventContext();
  const {
    associateTheTicketForMe,
    combosInNaming,
    updateCombosInNaming,
  } = useCartContext();
  const { toggleModal, setContentModal, defineTitleModal } = useModalContext();
  const { isMobile } = useUiContext();

  const validationSchema = yup.object<{ document: string }>({
    email : yup.string()
      .email(translate(['INFORMATION', 'INVALID_EMAIL']))
      .test('isNotMe', translate(['INFORMATION', 'CANNOT_TRANSFER_TO_MYSELF']), (value) => {
        if (holder) return true;
        return value !== user?.email;
      }),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize : true,
    onSubmit : async ({ email }) => {
      setUserNotFound(false);
      setUserHasntBepassToken(false);

      if(holder) {
        removeHolder(idSpecificInIdentifyList);
        formik.resetForm();
        return;
      }

      const userToNaming = await getUserByEmailOrDocument({ email, checkRfc : false, eventId : event?.id });
      const hasUser = userToNaming.length > 0;

      if (hasUser) return associateComboTicketToHolder({ ...userToNaming[0], email });

      const defaultIncompleteHolder = {
        id : Math.random(),
        name : email,
        lastName : email,
        password : email,
        nationality : 'BRA',
        email,
        picture : avatarDefaultUrl,
      };

      setUserNotFound(true);
      associateComboTicketToHolder(defaultIncompleteHolder);
    },
  });

  const hideErrorMessage = () => {
    setUserNotFound(false);
    formik.resetForm();
  };

  const transferRequiredRegisterFace = transferMandatoryFaceRecognition;
  const isBepassTicket = faceRecognition;
  const dontHasUserWarning = userNotFound && (isBepassTicket || transferRequiredRegisterFace);
  const containsError = (formik.errors.email && formik.touched.email) || dontHasUserWarning || userHasntBepassToken;
  const theresAHolderAndItsMe = (holder && user && holder.id === user.id) || false;
  const isAKidsTicket = false;

  const createRfcModal = () => {
    if (user) {
      defineTitleModal(translate(['INFORMATION', 'FACIAL_BIOMETRY_AVAILABLE']));
      setContentModal(
        <AlertModalContent
          associateTheTicketForMe={() =>
            associateTheTicketForMe({
              idSpecificInIdentifyList,
              user,
            })
          }
          isMandatory={transferMandatoryFaceRecognition}
        />
      );
      toggleModal({ open : true });
    }
  };

  // const createBepassAlreadyAssociatedModal = () => {
  //   defineTitleModal(translate(['INFORMATION', 'FACE_RECOGNITION_ASSOCIATION_ERROR_TITLE']));
  //   setContentModal(
  //     <div>
  //       {translate(['INFORMATION', 'ASSOCIATION_ERROR_FACE_RECOGNITION'])}
  //     </div>
  //   );
  //   toggleModal({ open : true });
  // };

  // const handleChecked = () => {
  //   if (!user) return;
  //   setUserHasntBepassToken(false);

  //   if (namingData.faceRecognition) {
  //     /* TODO - AINDA NÃO LIDAMOS COM RECONHECIMENTO FACIAL NO COMBO */
  //     // const hasAssociatedFaceInThisSession = associatedFaceRecognitionInThisSession(
  //     //   ticketsForThisEventInMyWallet,
  //     //   namingData,
  //     // );

  //     // if (hasAssociatedFaceInThisSession) return createBepassAlreadyAssociatedModal();
  //     // if (!user.faceRecognition.enabled) return createRfcModal();
  //   }

  //   associateTheTicketForMe({ idSpecificInIdentifyList : Number(namingData.id), user });
  //   formik.setFieldValue('email', '');
  // };

  useEffect(() => {
    if (startWithRfcModal) {
      createRfcModal();
    }
  }, []);

  const invalidHolder = formik.values.email === '' && !holder;
  const nonTransferable = !namingData.isTransferableGuestType;
  const nonTransferableCombo = namingData.comboDates.some(
    (comboDate) => comboDate.groups.some(
      (group) => group.tickets.some(
        (ticket) => !ticket.isTransferable
      )
    )
  );

  const disableNamingBtn = invalidHolder || nonTransferable || nonTransferableCombo;

  const associateComboTicketForMe = (): string => {
    const holder: HolderProps = {
      id : user?.id ?? 0,
      name : user?.name ?? '',
      email : user?.email ?? '',
      picture : user?.pictures?.[0].link ?? '',
    };

    const newCombosInNaming = combosInNaming.map((combo) => {
      if (combo.idSpecificInIdentifyList === idSpecificInIdentifyList) {
        return { ...combo, holder };
      }
      return combo;
    });
    updateCombosInNaming(newCombosInNaming);

    return holder.email;
  };

  const associateComboTicketToHolder = (holder : HolderProps) : string => {

    const newCombosInNaming = combosInNaming.map((combo) => {
      if (combo.idSpecificInIdentifyList === idSpecificInIdentifyList) {
        return { ...combo, holder };
      }
      return combo;
    });
    updateCombosInNaming(newCombosInNaming);

    return holder.email;
  };

  const removeHolder = (comboId: string) => {
    const newCombosInNaming = combosInNaming.map((combo) => {
      if (combo.idSpecificInIdentifyList === comboId) {
        return { ...combo, holder : null };
      }
      return combo;
    });
    updateCombosInNaming(newCombosInNaming);
    setUserNotFound(true);
  };

  const formattedComboDatesToTags = namingData.comboDates.map((date) => ({
    id : date.id,
    date : date.datetime,
    time : date.datetime,
    datetime : date.datetime,
    status : date.datetime,
  }));

  return (
    <Styles.WrapperNaming id={`naming-ticket-wrapper-${id}`}>
      <Styles.WrapperTicketInfo
        $error={userNotFound && !holder}
        id={`ticket-info-wrapper-${id}`}
      >
        <Styles.WrapperTicketTitle id={`ticket-title-wrapper-${id}`}>
          <Styles.ContainerTitle id={`ticket-title-container-${id}`}>
            <Styles.Title id={`ticket-title-${id}`}>
              {namingData.name}
            </Styles.Title>
            <Styles.Subtitle id={`ticket-subtitle-${id}`}>
              {`${namingData.comboDates.length} dias (${formatNumericValue({ value : sumValues(), currencyFormat })})`}
            </Styles.Subtitle>
            <DateTimeSessionsTags dates={formattedComboDatesToTags} />
          </Styles.ContainerTitle>
          <TagNaming
            transferMandatoryFaceRecognition={transferMandatoryFaceRecognition}
            faceRecognition={namingData.faceRecognition}
          />
        </Styles.WrapperTicketTitle>
        <Fragment>
          <Styles.WrapperInput id={`input-wrapper-${id}`}
            onSubmit={formik.handleSubmit}
            onBlur={formik.handleBlur}
          >
            <Styles.ContainerInput id={`input-container-${id}`}>
              {holder ? (
                <UserNaming
                  isAKidsTicket={isAKidsTicket}
                  theresAHolderAndItsMe={theresAHolderAndItsMe} holder={holder}
                />
              ) : (
                <Fragment>
                  {!nonTransferableCombo && (
                    <Fragment>
                      <Styles.InputForm
                        id={`input-email-${id}`}
                        name="email"
                        disabled={false}
                        $containsError={Boolean(containsError)}
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        type="text"
                        placeholder={translate(['INFORMATION', 'RECIPIENT_EMAIL'])}
                      />
                      <Styles.EmailIcon src={ImagesApp.svg.icEmail} id={`email-icon-${id}`} />
                    </Fragment>
                  )}
                </Fragment>
              )}
            </Styles.ContainerInput>
            {containsError && isMobile && (
              <Styles.ErrorText id='error-text-naming-email'>
                {dontHasUserWarning ? (
                  translate(['INFORMATION', 'ASSIGN_USER_HASNT_ACCOUNT_WARNING'])
                ) : formik.errors.email}
              </Styles.ErrorText>
            )}
            <Styles.NamingButton
              id={`naming-button-${id}`}
              disabled={disableNamingBtn}
              type='submit'
            >
              {translate(['INFORMATION', holder ? 'EDIT_ASSIGN' : 'TO_ASSIGN'])}
            </Styles.NamingButton>
            {containsError && !isMobile && (
              <Styles.ErrorText id='error-text-naming-email'>
                {dontHasUserWarning ? (
                  translate(['INFORMATION', 'ASSIGN_USER_HASNT_ACCOUNT_WARNING'])
                ) : formik.errors.email}
              </Styles.ErrorText>
            )}
          </Styles.WrapperInput>
          {userHasntBepassToken && <ErrorAlert hideErrorMessage={hideErrorMessage} />}
          {(!holder && !isAKidsTicket) && (
            <HolderTicketCheckbox
              handleChecked={associateComboTicketForMe}
              holder={holder}
              isAKidsTicket={false}
              sessions={formattedComboDatesToTags}
              isBepass={false}
              id={id}
            />
          )}
        </Fragment>
        {(holder && !theresAHolderAndItsMe) && (
          <Styles.TransferDescriptionParagraph>
            <span>
              {translate([
                'INFORMATION',
                isAKidsTicket ? (
                  'DEPENDENT_TRANSFER'
                ) : 'COMPANION_TRANSFER',
              ])}
            </span>
            <strong>
              {translate([
                'INFORMATION',
                isAKidsTicket ? (
                  'DEPENDENT_TRANSFER_BOLD'
                ) : 'COMPANION_TRANSFER_BOLD',
              ])}
            </strong>
          </Styles.TransferDescriptionParagraph>
        )}
      </Styles.WrapperTicketInfo>
    </Styles.WrapperNaming>
  );
};

export default NamingComboCard;
